import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useBooks } from '../contexts/BooksContext';
import { useAuth } from '../contexts/AuthContext';
import { isExcludedFromBestBook } from '../utils/constants';
import { WhopCheckoutEmbed } from '@whop/checkout/react';

const SettingsTakeover = ({ show, onHide, onShowCheckout, checkoutPlans, topOffset }) => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { enabledBooks, enabledBooksForBestBets, enabledBooksForGraphs, bookOrder, toggleBook, toggleBookForBestBets, toggleBookForGraphs, reorderBooks, bestBetsMaxDesktop, bestBetsMaxMobile, setBestBetsMaxDesktop, setBestBetsMaxMobile, bestBetsMinOdds, bestBetsMaxOdds, setBestBetsMinOdds, setBestBetsMaxOdds, bestBetsUseGlobalOdds, setBestBetsUseGlobalOdds, bestBetsOddsBySport, setBestBetsSportOdd, recalcEdgeOdds, setRecalcEdgeOdds, recalcEdgeBestBets, setRecalcEdgeBestBets, recalcEdgePlayer, setRecalcEdgePlayer } = useBooks();

  // Sports the odds-range gate applies to. Order matches the top nav.
  const BEST_BETS_SPORTS = [
    { key: 'mlb', label: 'MLB' },
    { key: 'nfl', label: 'NFL' },
    { key: 'ncaaf', label: 'NCAAF' },
    { key: 'wnba', label: 'WNBA' },
    { key: 'soccer', label: 'Soccer' },
    { key: 'ncaab', label: 'NCAAB' },
    { key: 'nba', label: 'NBA' },
    { key: 'nhl', label: 'NHL' },
  ];
  const { user, isAuthenticated, activeSub, logout, checkAuth } = useAuth();
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelStatus, setCancelStatus] = useState({ loading: false, error: null, success: false });
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null); // 'paid' or 'test'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Local state for Best Bets inputs to allow empty values during editing
  const [desktopInputValue, setDesktopInputValue] = useState(
    bestBetsMaxDesktop === null ? '' : String(bestBetsMaxDesktop)
  );
  const [mobileInputValue, setMobileInputValue] = useState(
    bestBetsMaxMobile === null ? '' : String(bestBetsMaxMobile)
  );
  const [minOddsInputValue, setMinOddsInputValue] = useState(
    bestBetsMinOdds === null ? '' : String(bestBetsMinOdds)
  );
  const [maxOddsInputValue, setMaxOddsInputValue] = useState(
    bestBetsMaxOdds === null ? '' : String(bestBetsMaxOdds)
  );

  // Sync local input state when context changes (e.g., when "All" is toggled)
  useEffect(() => {
    setDesktopInputValue(bestBetsMaxDesktop === null ? '' : String(bestBetsMaxDesktop));
  }, [bestBetsMaxDesktop]);

  useEffect(() => {
    setMobileInputValue(bestBetsMaxMobile === null ? '' : String(bestBetsMaxMobile));
  }, [bestBetsMaxMobile]);

  useEffect(() => {
    setMinOddsInputValue(bestBetsMinOdds === null ? '' : String(bestBetsMinOdds));
  }, [bestBetsMinOdds]);

  useEffect(() => {
    setMaxOddsInputValue(bestBetsMaxOdds === null ? '' : String(bestBetsMaxOdds));
  }, [bestBetsMaxOdds]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get display name for bookmakers
  const getBookDisplayName = (book) => {
    const displayNames = {
      fanduel: 'FanDuel',
      draftkings: 'DraftKings',
      betmgm: 'BetMGM',
      fanatics: 'Fanatics',
      espnbet: 'ESPN BET',
      betrivers: 'BetRivers',
      ballybet: 'Bally Bet',
      bovada: 'Bovada',
      caesars: 'Caesars',
      hardrockbet: 'Hard Rock Bet',
      bet365: 'bet365'
    };
    return displayNames[book] || book.charAt(0).toUpperCase() + book.slice(1);
  };

  // Move book up one position (mobile only)
  const moveBookUp = (index) => {
    if (index > 0) {
      reorderBooks(index, index - 1);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    setCancelStatus({ loading: true, error: null, success: false });

    try {
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      setCancelStatus({ loading: false, error: null, success: true });

      // Refresh user data to reflect cancellation
      setTimeout(() => {
        checkAuth();
        setShowCancelConfirm(false);
      }, 2000);

    } catch (error) {
      console.error('Cancel subscription error:', error);
      setCancelStatus({ loading: false, error: error.message, success: false });
    }
  };


  const isTrial = !activeSub || activeSub?.tier === 'trial' || activeSub?.tier === 'free';

  // Reset checkout state when modal is closed
  React.useEffect(() => {
    if (!show) {
      setShowCheckout(false);
      setCheckoutPlan(null);
    }
  }, [show]);


  if (!show) return null;
  
  return (
    <div
      className={`settings-takeover${isMobile ? '' : ' desktop-panel'}`}
      style={!isMobile && topOffset != null ? { top: `${topOffset}px` } : undefined}
    >
      <div className="settings-takeover-header">
        <h5 className="mb-0">Settings</h5>
        <Button
          variant="link"
          onClick={onHide}
          className="text-white p-0"
          style={{ fontSize: '1.5rem', lineHeight: 1 }}
        >
          ×
        </Button>
      </div>

      <div className={`settings-takeover-body${isMobile ? '' : ' settings-takeover-body-grid'}`}>
        {/* Account Section - only show if authenticated */}
        {isAuthenticated && user && (
          <div className="settings-section" data-col="1">
            <h6 className="settings-section-title">Account</h6>

            <div className="setting-item">
              {/* User Info Card */}
              <div style={{
                background: '#1e293b',
                border: '2px solid #2ecc71',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div className="d-flex align-items-center mb-3">
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: activeSub?.tier === 'paid' && activeSub?.status === 'past_due'
                      ? '#f97316'
                      : activeSub?.tier === 'paid' && (activeSub?.status === 'active' || activeSub?.status === 'trialing')
                        ? '#198754'
                        : (!activeSub || activeSub?.tier === 'trial' || activeSub?.tier === 'free'
                          ? '#000000'
                          : '#6c757d'),
                    backgroundImage: activeSub?.tier === 'paid' && activeSub?.status === 'past_due'
                      ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                      : activeSub?.tier === 'paid' && (activeSub?.status === 'active' || activeSub?.status === 'trialing')
                        ? 'linear-gradient(135deg, #198754 0%, #157347 100%)'
                        : (!activeSub || activeSub?.tier === 'trial' || activeSub?.tier === 'free'
                          ? 'linear-gradient(135deg, #000000 0%, #000000 100%)'
                          : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'),
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    border: '2px solid #94a3b8',
                    marginRight: '12px'
                  }}>
                    {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '16px', color: '#fff' }}>
                      {user.username || user.name || 'User'}
                    </div>
                    {user.email && (
                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                        {user.email}
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription Info */}
                <div style={{
                  background: '#0f172a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '12px',
                  border: '1px solid #334155'
                }}>
                  <div className="d-flex justify-content-center align-items-center mb-2">
                    <span style={{ fontSize: '13px', color: '#cbd5e1', marginRight: '8px' }}>Subscription:</span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2ecc71',
                      backgroundColor: 'rgba(46, 204, 113, 0.15)',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      border: '1px solid #2ecc71'
                    }}>
                      {activeSub?.displayName || 'No Active Subscription'}
                    </span>
                    {activeSub?.status && activeSub.status !== 'none' && (
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: activeSub.status === 'active' ? '#2ecc71' :
                               activeSub.status === 'trialing' ? '#fbbf24' :
                               activeSub.status === 'past_due' ? '#f97316' :
                               (activeSub.status === 'completed' && activeSub.tier === 'free') ? '#2ecc71' :
                               (activeSub.status === 'canceled' && activeSub.valid) ? '#3b82f6' :
                               (activeSub.status === 'canceled' || activeSub.status === 'completed') ? '#94a3b8' :
                               '#94a3b8',
                        backgroundColor: activeSub.status === 'active' ? 'rgba(46, 204, 113, 0.15)' :
                                        activeSub.status === 'trialing' ? 'rgba(251, 191, 36, 0.15)' :
                                        activeSub.status === 'past_due' ? 'rgba(249, 115, 22, 0.15)' :
                                        (activeSub.status === 'completed' && activeSub.tier === 'free') ? 'rgba(46, 204, 113, 0.15)' :
                                        (activeSub.status === 'canceled' && activeSub.valid) ? 'rgba(59, 130, 246, 0.15)' :
                                        'rgba(148, 163, 184, 0.15)',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        border: activeSub.status === 'active' ? '1px solid #2ecc71' :
                               activeSub.status === 'trialing' ? '1px solid #fbbf24' :
                               activeSub.status === 'past_due' ? '1px solid #f97316' :
                               (activeSub.status === 'completed' && activeSub.tier === 'free') ? '1px solid #2ecc71' :
                               (activeSub.status === 'canceled' && activeSub.valid) ? '1px solid #3b82f6' :
                               '1px solid #94a3b8',
                        marginLeft: '8px'
                      }}>
                        {activeSub.status === 'active' ? 'Active' :
                         activeSub.status === 'trialing' ? 'Trial' :
                         activeSub.status === 'past_due' ? 'On Hold' :
                         (activeSub.status === 'completed' && activeSub.tier === 'free') ? 'Active' :
                         (activeSub.status === 'canceled' && activeSub.valid) ? 'Ending Soon' :
                         (activeSub.status === 'canceled' || activeSub.status === 'completed') ? 'Expired' :
                         activeSub.status}
                      </span>
                    )}
                    {activeSub?.cancelAtPeriodEnd && activeSub?.canceledAt && (
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#f97316',
                        backgroundColor: 'rgba(249, 115, 22, 0.15)',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        border: '1px solid #f97316',
                        marginLeft: '8px'
                      }}>
                        Canceling
                      </span>
                    )}
                  </div>
                  {activeSub && (
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                      {activeSub.status === 'active'
                        ? 'Access to all games'
                        : activeSub.status === 'trialing'
                          ? (activeSub.tier === 'free' ? 'Limited to 1 game per sport' : 'Access to all games')
                          : activeSub.status === 'past_due'
                            ? 'Downgraded to free tier - 1 game limit'
                            : (activeSub.status === 'completed' && activeSub.tier === 'free')
                              ? 'Limited to 1 game per sport'
                              : (activeSub.status === 'canceled' && activeSub.valid)
                                ? 'Access until subscription ends'
                                : 'No active access'}
                    </div>
                  )}
                  {activeSub && (
                    <div style={{
                      fontSize: '11px',
                      color: '#94a3b8',
                      textAlign: 'center'
                    }}>
                      {(() => {
                        // For subscriptions that are being canceled
                        if (activeSub.cancelAtPeriodEnd && activeSub.canceledAt && activeSub.renewalPeriodEnd) {
                          const endDate = new Date(activeSub.renewalPeriodEnd);
                          const now = new Date();
                          const hoursRemaining = (endDate - now) / (1000 * 60 * 60);

                          // If less than 24 hours, show hours remaining
                          if (hoursRemaining < 24 && hoursRemaining > 0) {
                            const hours = Math.ceil(hoursRemaining);
                            return `Your access to simpleOdds.app will end in ${hours} hour${hours !== 1 ? 's' : ''}`;
                          }

                          // Otherwise show the date in Eastern Time
                          const formattedDate = endDate.toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                            timeZone: 'America/New_York'
                          });
                          return `Your access to simpleOdds.app will end on ${formattedDate}`;
                        }

                        // For trialing subscriptions
                        if (activeSub.status === 'trialing' && activeSub.renewalPeriodEnd) {
                          const trialEnd = new Date(activeSub.renewalPeriodEnd);
                          const now = new Date();
                          const hoursRemaining = (trialEnd - now) / (1000 * 60 * 60);

                          // If less than 24 hours, show hours remaining
                          if (hoursRemaining < 24 && hoursRemaining > 0) {
                            const hours = Math.ceil(hoursRemaining);
                            return `Trial ends in ${hours} hour${hours !== 1 ? 's' : ''}`;
                          }

                          // Otherwise show the date in Eastern Time
                          const formattedDate = trialEnd.toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                            timeZone: 'America/New_York'
                          });
                          return `Trial ends/Paid Subscription begins on ${formattedDate}`;
                        }

                        // For free tier subscriptions
                        if (activeSub.tier === 'free' && activeSub.expiresAt) {
                          const expiresDate = new Date(activeSub.expiresAt);
                          const formattedDate = expiresDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                          return `Expires ${formattedDate}`;
                        }

                        // For paid subscriptions with renewal date
                        if (activeSub.renewalPeriodEnd) {
                          const renewalDate = new Date(activeSub.renewalPeriodEnd);
                          const formattedDate = renewalDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                          return `(${activeSub.status === 'active' && !activeSub.cancelAtPeriodEnd ? 'renews' : 'exp'}. ${formattedDate})`;
                        }

                        return null;
                      })()}
                    </div>
                  )}
                  {activeSub?.tier === 'free' && (
                    <div style={{
                      fontSize: '10px',
                      color: '#94a3b8',
                      textAlign: 'center',
                      marginTop: '6px',
                      fontStyle: 'italic'
                    }}>
                      * Free tier can be renewed upon expiration. We ask users to renew manually to minimize ghost accounts.
                    </div>
                  )}
                </div>

                {/* Account on Hold Warning for Past Due Subscriptions */}
                {activeSub?.status === 'past_due' && (
                  <div style={{
                    background: 'rgba(249, 115, 22, 0.1)',
                    border: '2px solid #f97316',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      marginBottom: '8px'
                    }}>
                      ⚠️
                    </div>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#f97316',
                      marginBottom: '8px'
                    }}>
                      Account on Hold
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#cbd5e1',
                      lineHeight: '1.6',
                      marginBottom: '12px'
                    }}>
                      Your payment method was declined. Your account has been reverted to free tier access until payment is updated.
                    </div>
                    <a
                      href="https://whop.com/hub/simpleodds/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: '#f97316',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#ea580c'}
                      onMouseLeave={(e) => e.target.style.background = '#f97316'}
                    >
                      Update Payment to Restore Access
                    </a>
                  </div>
                )}

                {/* Upgrade Section for Trial Users */}
                {isTrial && !showCheckout && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
                    border: '2px solid #3b82f6',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#94a3b8',
                      marginBottom: '8px',
                      fontWeight: '600'
                    }}>
                      Upgrade to Full Access
                    </div>

                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#fff',
                      marginBottom: '4px'
                    }}>
                      ${checkoutPlans?.paid?.cost || 15}/mo
                    </div>

                    <div style={{
                      fontSize: '12px',
                      color: '#2ecc71',
                      fontWeight: '600',
                      marginBottom: '12px',
                      padding: '4px 10px',
                      background: 'rgba(46, 204, 113, 0.15)',
                      borderRadius: '6px',
                      display: 'inline-block'
                    }}>
                      7-Day Free Trial
                    </div>

                    <div style={{
                      fontSize: '13px',
                      color: '#cbd5e1',
                      marginBottom: '16px',
                      lineHeight: '1.4'
                    }}>
                      Get unlimited access to all games and premium features
                    </div>

                    <Button
                      size="md"
                      onClick={() => {
                        const planId = checkoutPlans?.paid?.plan;
                        if (planId) {
                          window.location.assign(`https://whop.com/checkout/${planId}/`);
                        }
                      }}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '15px',
                        padding: '10px',
                        marginBottom: '8px',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 16px rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      Get Full Access
                    </Button>

                    <div style={{
                      fontSize: '10px',
                      color: '#94a3b8',
                      marginTop: '8px'
                    }}>
                      Cancel anytime. No commitment required.
                    </div>
                  </div>
                )}

                {/* Whop Checkout Embed */}
                {isTrial && showCheckout && (
                  <div style={{
                    background: '#0f172a',
                    borderRadius: '8px',
                    padding: '16px 8px',
                    marginBottom: '12px',
                    border: '1px solid #334155'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#fff'
                      }}>
                        {checkoutPlan === 'test' ? 'Test Checkout' : 'Complete Your Purchase'}
                      </div>
                      <button
                        onClick={() => {
                          setShowCheckout(false);
                          setCheckoutPlan(null);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: '0',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {!checkoutPlans ? (
                      <div style={{
                        color: '#cbd5e1',
                        padding: '40px 20px',
                        textAlign: 'center'
                      }}>
                        Loading checkout...
                      </div>
                    ) : (
                      <WhopCheckoutEmbed
                        planId={checkoutPlans.paid.plan}
                        theme="dark"
                        prefill={{
                          email: user?.email || ''
                        }}
                      onComplete={async (planId, receiptId) => {
                        console.log('Checkout completed:', planId, receiptId);

                        // Send checkout completion to backend and wait for it to complete
                        try {
                          const response = await fetch('/api/auth/checkout', {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ planId, receiptId })
                          });

                          if (!response.ok) {
                            console.error('Checkout completion API failed:', await response.text());
                          } else {
                            // Wait for the response to complete
                            await response.json();
                          }
                        } catch (error) {
                          console.error('Error sending checkout completion:', error);
                        }

                        // Refresh auth to get updated subscription (only after checkout endpoint completes)
                        await checkAuth();
                        setShowCheckout(false);
                        setCheckoutPlan(null);
                        // Optionally close the settings modal
                        // onHide();
                      }}
                    />
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  {(activeSub?.status === 'active' || activeSub?.status === 'trialing') && activeSub?.tier === 'paid' ? (
                    // Paid active/trialing status: Show logout link and cancel subscription link
                    <>
                      <div>
                        <a
                          onClick={logout}
                          style={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          Logout
                        </a>
                      </div>
                      {/* Only show cancel link if not already canceled */}
                      {!(activeSub?.cancelAtPeriodEnd && activeSub?.canceledAt) && (
                        <div style={{ marginTop: '12px' }}>
                          <a
                            href="https://whop.com/@me/settings/orders/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#cbd5e1',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              background: 'rgba(148, 163, 184, 0.1)',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid #334155',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(148, 163, 184, 0.2)';
                              e.target.style.borderColor = '#94a3b8';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(148, 163, 184, 0.1)';
                              e.target.style.borderColor = '#334155';
                            }}
                          >
                            <svg width="20" height="10" viewBox="0 0 36 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path clipRule="evenodd" d="M5.73835 0.0180664C3.37553 0.0180664 1.75344 1.02402 0.515773 2.16716C0.515773 2.16716 0.0188309 2.62441 0.0282072 2.6427L5.20391 7.69078L10.3796 2.6427C9.3951 1.32581 7.54797 0.0180664 5.73835 0.0180664ZM18.5182 0.0180664C16.1554 0.0180664 14.5333 1.03317 13.2956 2.16716C13.2956 2.16716 12.8455 2.61527 12.8174 2.6427L6.42279 8.87964L11.5891 13.9186L23.15 2.6427C22.1655 1.32581 20.3184 0.0180664 18.5182 0.0180664ZM26.1036 2.16716C27.3413 1.03317 28.9634 0.0180664 31.3262 0.0180664C33.1358 0.0180664 34.9829 1.32581 35.9581 2.6427H35.9393L21.7905 16.4335C19.6902 18.4728 16.2585 18.4728 14.1676 16.4335L12.8174 15.1166L25.616 2.6427C25.6348 2.61527 26.1036 2.16716 26.1036 2.16716Z" fill="currentColor" fillRule="evenodd"/>
                            </svg>
                            Manage Subscription
                          </a>
                        </div>
                      )}
                      <div style={{ marginTop: '12px' }}>
                        <a
                          href="https://whop.com/@me/settings/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#cbd5e1',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            background: 'rgba(148, 163, 184, 0.1)',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #334155',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(148, 163, 184, 0.2)';
                            e.target.style.borderColor = '#94a3b8';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(148, 163, 184, 0.1)';
                            e.target.style.borderColor = '#334155';
                          }}
                        >
                          <svg width="20" height="10" viewBox="0 0 36 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path clipRule="evenodd" d="M5.73835 0.0180664C3.37553 0.0180664 1.75344 1.02402 0.515773 2.16716C0.515773 2.16716 0.0188309 2.62441 0.0282072 2.6427L5.20391 7.69078L10.3796 2.6427C9.3951 1.32581 7.54797 0.0180664 5.73835 0.0180664ZM18.5182 0.0180664C16.1554 0.0180664 14.5333 1.03317 13.2956 2.16716C13.2956 2.16716 12.8455 2.61527 12.8174 2.6427L6.42279 8.87964L11.5891 13.9186L23.15 2.6427C22.1655 1.32581 20.3184 0.0180664 18.5182 0.0180664ZM26.1036 2.16716C27.3413 1.03317 28.9634 0.0180664 31.3262 0.0180664C33.1358 0.0180664 34.9829 1.32581 35.9581 2.6427H35.9393L21.7905 16.4335C19.6902 18.4728 16.2585 18.4728 14.1676 16.4335L12.8174 15.1166L25.616 2.6427C25.6348 2.61527 26.1036 2.16716 26.1036 2.16716Z" fill="currentColor" fillRule="evenodd"/>
                          </svg>
                          Whop Account Settings
                        </a>
                      </div>
                    </>
                  ) : (
                    // Trial/None: Show Logout link only
                    <>
                      <div>
                        <a
                          onClick={logout}
                          style={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          Logout
                        </a>
                      </div>
                      <div style={{ marginTop: '12px' }}>
                        <a
                          href="https://whop.com/@me/settings/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#cbd5e1',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            background: 'rgba(148, 163, 184, 0.1)',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #334155',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(148, 163, 184, 0.2)';
                            e.target.style.borderColor = '#94a3b8';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(148, 163, 184, 0.1)';
                            e.target.style.borderColor = '#334155';
                          }}
                        >
                          <svg width="20" height="10" viewBox="0 0 36 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path clipRule="evenodd" d="M5.73835 0.0180664C3.37553 0.0180664 1.75344 1.02402 0.515773 2.16716C0.515773 2.16716 0.0188309 2.62441 0.0282072 2.6427L5.20391 7.69078L10.3796 2.6427C9.3951 1.32581 7.54797 0.0180664 5.73835 0.0180664ZM18.5182 0.0180664C16.1554 0.0180664 14.5333 1.03317 13.2956 2.16716C13.2956 2.16716 12.8455 2.61527 12.8174 2.6427L6.42279 8.87964L11.5891 13.9186L23.15 2.6427C22.1655 1.32581 20.3184 0.0180664 18.5182 0.0180664ZM26.1036 2.16716C27.3413 1.03317 28.9634 0.0180664 31.3262 0.0180664C33.1358 0.0180664 34.9829 1.32581 35.9581 2.6427H35.9393L21.7905 16.4335C19.6902 18.4728 16.2585 18.4728 14.1676 16.4335L12.8174 15.1166L25.616 2.6427C25.6348 2.61527 26.1036 2.16716 26.1036 2.16716Z" fill="currentColor" fillRule="evenodd"/>
                          </svg>
                          Whop Account Settings
                        </a>
                        {activeSub?.status === 'past_due' && (
                          <div style={{
                            fontSize: '11px',
                            color: '#94a3b8',
                            fontStyle: 'italic',
                            marginTop: '8px',
                            lineHeight: '1.4'
                          }}>
                            NOTE: Payment updates for past due accounts may take up to 5 minutes to sync.
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Cancellation Confirmation Block */}
                {showCancelConfirm && (
                  <div style={{
                    background: '#1e293b',
                    border: '2px solid #ef4444',
                    borderRadius: '12px',
                    padding: '20px',
                    marginTop: '16px'
                  }}>
                    <h6 style={{ color: '#ef4444', marginBottom: '12px', fontWeight: '600' }}>
                      {isTrial ? 'End Trial Access?' : 'Cancel Subscription?'}
                    </h6>

                    {/* Expiration Date Display */}
                    {activeSub?.renewalPeriodEnd && (
                      <div style={{
                        background: '#0f172a',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        marginBottom: '12px',
                        border: '1px solid #334155'
                      }}>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
                          Current Expiration Date:
                        </div>
                        <div style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>
                          {new Date(activeSub.renewalPeriodEnd).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    )}

                    <p style={{ fontSize: '14px', color: '#cbd5e1', marginBottom: '16px' }}>
                      {isTrial ? (
                        <>
                          Your trial access will be <strong style={{ color: '#fff' }}>revoked immediately</strong>.
                        </>
                      ) : (
                        activeSub?.renewalPeriodEnd ? (
                          <>
                            Your subscription will be cancelled, but you'll retain access until{' '}
                            <strong style={{ color: '#fff' }}>
                              {new Date(activeSub.renewalPeriodEnd).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </strong>. After that, you'll lose access to premium features.
                          </>
                        ) : (
                          <>
                            Your subscription will be cancelled, but you'll retain access until the end of your billing period. After that, you'll lose access to premium features.
                          </>
                        )
                      )}
                    </p>

                    {cancelStatus.error && (
                      <div style={{
                        background: '#7f1d1d',
                        border: '1px solid #ef4444',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '12px'
                      }}>
                        <p style={{ fontSize: '13px', color: '#fca5a5', margin: 0 }}>
                          ⚠️ {cancelStatus.error}
                        </p>
                      </div>
                    )}

                    {cancelStatus.success && (
                      <div style={{
                        background: '#14532d',
                        border: '1px solid #22c55e',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '12px'
                      }}>
                        <p style={{ fontSize: '13px', color: '#86efac', margin: 0 }}>
                          ✓ {isTrial ? 'Trial ended successfully' : 'Subscription cancelled successfully'}
                        </p>
                      </div>
                    )}

                    <div className="d-grid gap-2">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleCancelSubscription}
                        disabled={cancelStatus.loading || cancelStatus.success}
                      >
                        {cancelStatus.loading ? 'Processing...' : isTrial ? 'Yes, End Trial Now' : 'Yes, Cancel Subscription'}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setShowCancelConfirm(false);
                          setCancelStatus({ loading: false, error: null, success: false });
                        }}
                        disabled={cancelStatus.loading}
                      >
                        Nevermind, Keep Subscription
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Appearance section - hidden for now (dark mode only) */}
        {/*
        <div className="settings-section">
          <h6 className="settings-section-title">Appearance</h6>

          <div className="setting-item">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="setting-label">Dark Mode</div>
                <small className="setting-description text-muted">
                  Switch between light and dark themes
                </small>
              </div>
              <Form.Check
                type="switch"
                id="dark-mode-switch"
                checked={isDarkMode}
                onChange={toggleDarkMode}
                className="setting-switch"
              />
            </div>
          </div>
        </div>
        */}

        {/* My Books Section - always show for authenticated users */}
        {isAuthenticated && (
          <div className="settings-section" data-col="2">
            <h6 className="settings-section-title">My Books</h6>
          
          <div className="setting-item">
            <div className="setting-label">Sportsbooks</div>
            <small className="setting-description text-muted d-block mb-3">
              Toggle which sportsbooks to display in odds tables and best bets
            </small>

            <div className="books-grid">
              {(() => {
                const firstSecondaryIdx = bookOrder.findIndex(b => isExcludedFromBestBook(b));
                return bookOrder.map((book, index) => (
                <React.Fragment key={book}>
                  {firstSecondaryIdx !== -1 && index === firstSecondaryIdx && (
                    <div className="books-secondary-divider">
                      <div className="books-secondary-divider-label">Secondary Books</div>
                      <small className="books-secondary-divider-blurb">
                        These books don't participate in the best book calculation. When their odds tie or beat the primary best, the cell is marked with a colored corner caret indicating the edge.
                      </small>
                    </div>
                  )}
                <div
                  className={`book-toggle-item ${
                    !isMobile && draggedItem === index ? 'dragging' : ''
                  } ${
                    !isMobile && dragOverIndex === index ? 'drag-over' : ''
                  }`}
                  draggable={!isMobile}
                  onDragStart={!isMobile ? (e) => {
                    setDraggedItem(index);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/html', e.target);
                  } : undefined}
                  onDragEnd={!isMobile ? () => {
                    setDraggedItem(null);
                    setDragOverIndex(null);
                  } : undefined}
                  onDragOver={!isMobile ? (e) => {
                    e.preventDefault();
                    setDragOverIndex(index);
                  } : undefined}
                  onDragLeave={!isMobile ? () => {
                    setDragOverIndex(null);
                  } : undefined}
                  onDrop={!isMobile ? (e) => {
                    e.preventDefault();
                    if (draggedItem !== null && draggedItem !== index) {
                      reorderBooks(draggedItem, index);
                    }
                    setDraggedItem(null);
                    setDragOverIndex(null);
                  } : undefined}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      {isMobile ? (
                        // Mobile: Show up arrow button
                        <button
                          onClick={() => moveBookUp(index)}
                          disabled={index === 0}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: index === 0 ? 'transparent' : '#94a3b8',
                            fontSize: '18px',
                            cursor: index === 0 ? 'default' : 'pointer',
                            padding: '0 8px 0 0',
                            marginRight: '4px',
                            lineHeight: 1
                          }}
                        >
                          ↑
                        </button>
                      ) : (
                        // Desktop: Show drag handle
                        <div className="drag-handle">
                          ☰
                        </div>
                      )}
                      <img
                        src={`/logos/${book}.png`}
                        alt={getBookDisplayName(book)}
                        style={{ height: '20px', marginRight: '8px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <span className="book-name">{getBookDisplayName(book)}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div className="d-flex flex-column align-items-center" style={{ minWidth: '70px' }}>
                        <small style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: '2px' }}>Main</small>
                        <Form.Check
                          type="switch"
                          id={`book-main-${book}`}
                          checked={enabledBooks[book] !== false}
                          onChange={() => toggleBook(book)}
                          className="book-switch"
                        />
                      </div>
                      <div className="d-flex flex-column align-items-center" style={{ minWidth: '70px' }}>
                        <small style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: '2px' }}>Best Bets</small>
                        <Form.Check
                          type="switch"
                          id={`book-bestbets-${book}`}
                          checked={enabledBooksForBestBets[book] !== false}
                          onChange={() => toggleBookForBestBets(book)}
                          className="book-switch"
                        />
                      </div>
                      <div className="d-flex flex-column align-items-center" style={{ minWidth: '70px' }}>
                        <small style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: '2px' }}>Graphs</small>
                        <Form.Check
                          type="switch"
                          id={`book-graphs-${book}`}
                          checked={enabledBooksForGraphs?.[book] !== false}
                          onChange={() => toggleBookForGraphs(book)}
                          className="book-switch"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                </React.Fragment>
                ));
              })()}
            </div>
          </div>
          </div>
        )}

        {/* Best Bets Settings Section */}
        {isAuthenticated && (
          <div className="settings-section" data-col="3">
            <h6 className="settings-section-title">Best Bets</h6>

            <div className="setting-item">
              <div className="setting-label">Max Results</div>
              <small className="setting-description text-muted d-block mb-3">
                Limit the number of best bets shown for better performance
              </small>

              {/* Desktop Max Results */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
                padding: '10px 12px',
                background: '#1e293b',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#cbd5e1', fontSize: '14px', minWidth: '70px' }}>Desktop:</span>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#94a3b8',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={bestBetsMaxDesktop === null}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBestBetsMaxDesktop(null);
                      } else {
                        setBestBetsMaxDesktop(1000); // Default when unchecking "All"
                      }
                    }}
                    style={{ width: '16px', height: '16px' }}
                  />
                  All
                </label>
                <input
                  type="number"
                  min="1"
                  max="9999"
                  value={desktopInputValue}
                  onChange={(e) => {
                    setDesktopInputValue(e.target.value);
                    const num = parseInt(e.target.value, 10);
                    if (!isNaN(num) && num > 0) {
                      setBestBetsMaxDesktop(num);
                    }
                  }}
                  onBlur={() => {
                    // Reset to valid value if empty or invalid on blur
                    const num = parseInt(desktopInputValue, 10);
                    if (isNaN(num) || num <= 0) {
                      setDesktopInputValue('1000');
                      setBestBetsMaxDesktop(1000);
                    }
                  }}
                  disabled={bestBetsMaxDesktop === null}
                  placeholder="1000"
                  style={{
                    width: '80px',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #334155',
                    background: bestBetsMaxDesktop === null ? '#0f172a' : '#1e293b',
                    color: bestBetsMaxDesktop === null ? '#64748b' : '#fff',
                    fontSize: '14px',
                    opacity: bestBetsMaxDesktop === null ? 0.5 : 1
                  }}
                />
              </div>

              {/* Mobile Max Results */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 12px',
                background: '#1e293b',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#cbd5e1', fontSize: '14px', minWidth: '70px' }}>Mobile:</span>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#94a3b8',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={bestBetsMaxMobile === null}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBestBetsMaxMobile(null);
                      } else {
                        setBestBetsMaxMobile(500); // Default when unchecking "All"
                      }
                    }}
                    style={{ width: '16px', height: '16px' }}
                  />
                  All
                </label>
                <input
                  type="number"
                  min="1"
                  max="9999"
                  value={mobileInputValue}
                  onChange={(e) => {
                    setMobileInputValue(e.target.value);
                    const num = parseInt(e.target.value, 10);
                    if (!isNaN(num) && num > 0) {
                      setBestBetsMaxMobile(num);
                    }
                  }}
                  onBlur={() => {
                    // Reset to valid value if empty or invalid on blur
                    const num = parseInt(mobileInputValue, 10);
                    if (isNaN(num) || num <= 0) {
                      setMobileInputValue('500');
                      setBestBetsMaxMobile(500);
                    }
                  }}
                  disabled={bestBetsMaxMobile === null}
                  placeholder="500"
                  style={{
                    width: '80px',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #334155',
                    background: bestBetsMaxMobile === null ? '#0f172a' : '#1e293b',
                    color: bestBetsMaxMobile === null ? '#64748b' : '#fff',
                    fontSize: '14px',
                    opacity: bestBetsMaxMobile === null ? 0.5 : 1
                  }}
                />
              </div>
            </div>

            <div className="setting-item" style={{ marginTop: '16px' }}>
              <div className="setting-label">Odds Range</div>
              <small className="setting-description text-muted d-block mb-3">
                Only include plays with American odds inside the range. Leave a field blank to remove that bound.
              </small>

              {/* Global-vs-per-sport toggle */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                marginBottom: '12px',
                background: '#1e293b',
                borderRadius: '8px',
                cursor: 'pointer',
                color: '#cbd5e1',
                fontSize: '14px'
              }}>
                <input
                  type="checkbox"
                  checked={bestBetsUseGlobalOdds}
                  onChange={(e) => setBestBetsUseGlobalOdds(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                Apply one range to all sports
              </label>

              {bestBetsUseGlobalOdds ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  background: '#1e293b',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#cbd5e1', fontSize: '14px', minWidth: '70px' }}>Min:</span>
                  <input
                    type="number"
                    step="1"
                    value={minOddsInputValue}
                    onChange={(e) => {
                      setMinOddsInputValue(e.target.value);
                      if (e.target.value === '' || e.target.value === '-') {
                        setBestBetsMinOdds(null);
                      } else {
                        const num = parseInt(e.target.value, 10);
                        if (!Number.isNaN(num)) setBestBetsMinOdds(num);
                      }
                    }}
                    placeholder="e.g. -300"
                    style={{
                      width: '110px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #334155',
                      background: '#1e293b',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{ color: '#cbd5e1', fontSize: '14px', minWidth: '50px', marginLeft: '8px' }}>Max:</span>
                  <input
                    type="number"
                    step="1"
                    value={maxOddsInputValue}
                    onChange={(e) => {
                      setMaxOddsInputValue(e.target.value);
                      if (e.target.value === '' || e.target.value === '-') {
                        setBestBetsMaxOdds(null);
                      } else {
                        const num = parseInt(e.target.value, 10);
                        if (!Number.isNaN(num)) setBestBetsMaxOdds(num);
                      }
                    }}
                    placeholder="e.g. +200"
                    style={{
                      width: '110px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #334155',
                      background: '#1e293b',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {BEST_BETS_SPORTS.map(({ key, label }) => {
                    const entry = bestBetsOddsBySport[key] || {};
                    const minValue = entry.min === null || entry.min === undefined ? '' : String(entry.min);
                    const maxValue = entry.max === null || entry.max === undefined ? '' : String(entry.max);
                    return (
                      <div key={key} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        background: '#1e293b',
                        borderRadius: '8px'
                      }}>
                        <span style={{ color: '#cbd5e1', fontSize: '13px', minWidth: '60px', fontWeight: 600 }}>{label}</span>
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Min:</span>
                        <input
                          type="number"
                          step="1"
                          value={minValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || v === '-') {
                              setBestBetsSportOdd(key, 'min', null);
                            } else {
                              const num = parseInt(v, 10);
                              if (!Number.isNaN(num)) setBestBetsSportOdd(key, 'min', num);
                            }
                          }}
                          placeholder="—"
                          style={{
                            width: '90px',
                            padding: '5px 8px',
                            borderRadius: '6px',
                            border: '1px solid #334155',
                            background: '#0f172a',
                            color: '#fff',
                            fontSize: '13px'
                          }}
                        />
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Max:</span>
                        <input
                          type="number"
                          step="1"
                          value={maxValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || v === '-') {
                              setBestBetsSportOdd(key, 'max', null);
                            } else {
                              const num = parseInt(v, 10);
                              if (!Number.isNaN(num)) setBestBetsSportOdd(key, 'max', num);
                            }
                          }}
                          placeholder="—"
                          style={{
                            width: '90px',
                            padding: '5px 8px',
                            borderRadius: '6px',
                            border: '1px solid #334155',
                            background: '#0f172a',
                            color: '#fff',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="setting-item" style={{ marginTop: '16px' }}>
              <div className="setting-label">Edge Calculation</div>
              <small className="setting-description text-muted d-block mb-3">
                Recalculate edges using only your enabled books instead of server defaults (all books)
              </small>

              {/* Odds Screen toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                background: '#1e293b',
                borderRadius: '8px',
                marginBottom: '8px'
              }}>
                <span style={{ color: '#cbd5e1', fontSize: '14px' }}>Odds Screen</span>
                <Form.Check
                  type="switch"
                  id="recalc-edge-odds"
                  checked={recalcEdgeOdds}
                  onChange={() => setRecalcEdgeOdds(!recalcEdgeOdds)}
                  className="book-switch"
                />
              </div>

              {/* Best Bets toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                background: '#1e293b',
                borderRadius: '8px',
                marginBottom: '8px'
              }}>
                <span style={{ color: '#cbd5e1', fontSize: '14px' }}>Best Bets</span>
                <Form.Check
                  type="switch"
                  id="recalc-edge-bestbets"
                  checked={recalcEdgeBestBets}
                  onChange={() => setRecalcEdgeBestBets(!recalcEdgeBestBets)}
                  className="book-switch"
                />
              </div>

              {/* Player Screen toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                background: '#1e293b',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#cbd5e1', fontSize: '14px' }}>Player Screen</span>
                <Form.Check
                  type="switch"
                  id="recalc-edge-player"
                  checked={recalcEdgePlayer}
                  onChange={() => setRecalcEdgePlayer(!recalcEdgePlayer)}
                  className="book-switch"
                />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SettingsTakeover;